import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import dynamic from 'next/dynamic';
import { Button } from '@material-ui/core';
import { useRouter } from 'next/router';
import _, { get } from 'lodash';
import { Camera, ArrowsDownUp, CaretDown, CaretUp } from 'phosphor-react';

import { searchFilterState } from '../constants';
import EventEmitter from '../../../../lib/eventEmitter';
import { SortAscending, SortDescending } from 'phosphor-react';
import { UpdateFilterDialog } from '../../../../actions/actionTypes';
import {
  feedFetchedHelper,
  sortFeedHelper,
  fetchMapFeed,
  updateFilterSearch,
} from '../../../../actions/feed';
import { ROOM_TYPES } from '../Filters/RoomCondition';

const SortByGeneral = dynamic(() => import('../Sorting/SortByGeneral'));
const SortByPhoto = dynamic(() => import('../Sorting/SortByPhoto'));

import styles from './styles';

const DEFAULT_MAX_PRICE = 1000000000;

export interface Props {
  feed: any;
  feedFetched: boolean;
  feedFetchedHelper: Function;
  feedSearch: any;
  filters: any;
  mobile: boolean;
  sortFeedHelper: Function;
  fetchMapFeed: Function;
  filterRoomPhotoOrderOpen: boolean;
  filterSortOptionsOpen: boolean;
  token: string;
  usePlaceBoundary: boolean;
  updateFilter: Function;
  updateFilterDialog: Function;
}

export interface State {
  label: string;
  priceDirty: boolean;
  sortAsc: boolean;
  sortBy: string;
  showAverages: boolean;
}

export function SortForm(props: Props) {
  const ref = useRef<any>();
  const router = useRouter();

  const classes = styles(props);
  const {
    feed,
    mobile,
    filters,
    filterRoomPhotoOrderOpen,
    filterSortOptionsOpen,
    feedSearch,
    updateFilter,
    updateFilterDialog,
  } = props;
  const [initialLoad, setInitialLoad] = useState(true);
  const [state, setState] = useState({
    label: '',
    priceDirty: false,
    showAverages: false,
    sortAsc: true,
    sortBy: '',
  });

  const handleClick = (e: any) => {
    if (e.target.id === 'sort button') {
      return;
    }
    if (ref.current && !ref.current.contains(e.target)) {
      return setState(oldValues => ({
        ...oldValues,
        showAverages: false,
        showSortOptions: false,
      }));
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClick, false);
    if (initialLoad) {
      const filters = JSON.parse(get(router, 'query.filters', '{}'));
      if (Object.keys(filters).length > 0) {
        preFillFilters(filters);
      } else {
        preFillFilters(props.feedSearch);
      }
    }
    setTimeout(() => {
      setInitialLoad(false);
    }, 0);
    return () => {
      document.removeEventListener('mousedown', handleClick, false);
    };
  }, []);

  // Use same sorting option on new feeds
  useEffect(() => {
    sortFeed(state.sortBy, state.sortAsc);
  }, [props.feed]);

  const toggleSortOption = (sortType: string) => {
    updateFilterDialog(sortType);
  };

  useEffect(() => {
    sortFeed(state.sortBy, state.sortAsc);
  }, [state.sortBy, state.sortAsc]);

  const preFillFilters = (feedSearch: any) => {
    const fetchRoomConditons = (feedSearch: any) => {
      if (feedSearch.conditionsByRoomType) {
        if (_.isString(feedSearch.conditionsByRoomType)) {
          return JSON.parse(feedSearch.conditionsByRoomType);
        } else if (_.isArray(feedSearch.conditionsByRoomType)) {
          return feedSearch.conditionsByRoomType;
        }
      }
      return [];
    };
    if (feedSearch) {
      updateFilter({
        photosByRoom: feedSearch.photosByRoom
          ? feedSearch.photosByRoom
          : searchFilterState.photosByRoom,
        conditionsByRoomType: fetchRoomConditons(feedSearch),
      });
    }
  };

  const handleSubmit = (stateCopy: any) => {
    const values: any = Object.assign({}, stateCopy);
    values.propType = [];
    values.location = props.feedSearch.location;
    values.boundingBox = props.feedSearch.boundingBox;
    values.polygonBoundaries = props.feedSearch.polygonBoundaries;
    values.single &&
      values.propType.indexOf('Single Family') < 0 &&
      values.propType.push('Single Family');
    values.multi &&
      values.propType.indexOf('Multi-Family') < 0 &&
      values.propType.push('Multi-Family');
    values.condo &&
      values.propType.indexOf('Condominium/Co-Op') < 0 &&
      values.propType.push('Condominium/Co-Op');
    if (props.feedSearch.locationName) {
      values.locationName = props.feedSearch.locationName;
    }
    if (values.minAge === 0) {
      delete values.minAge;
    }
    if (values.maxAge === 0) {
      delete values.maxAge;
    }
    if (values.minPrice === 0) {
      delete values.minPrice;
    }
    if (values.maxPrice === DEFAULT_MAX_PRICE) {
      delete values.maxPrice;
    }
    if (values.conditionsByRoomType) {
      values.conditionsByRoomType = JSON.stringify(values.conditionsByRoomType);
    }
    // Remove unnecessary properties so that only required values can be stored in DB
    delete values.priceRange;
    delete values.yearRange;
    delete values.single;
    delete values.multi;
    delete values.condo;

    props.fetchMapFeed(
      values.location,
      props.token,
      values.boundingBox,
      true,
      values,
      props.usePlaceBoundary,
    );
  };

  const onSortChange = (label: string, sortBy: string) => {
    EventEmitter.emit('onSortChange');
    if (sortBy === state.sortBy) {
      setState(oldValues => ({
        ...oldValues,
        label,
        sortAsc: !oldValues.sortAsc,
      }));
    } else {
      setState(oldValues => ({
        ...oldValues,
        label,
        sortAsc: true,
        sortBy,
        showSortOptions: false,
      }));
    }
  };

  const handleRoomFilter = (value: string) => {
    if (value !== filters.photosByRoom) {
      updateFilter({
        photosByRoom: value,
      });
    }
  };

  const handleRoomQualityFilter = (conditionsByRoomType: any) => {
    if (
      JSON.stringify(conditionsByRoomType) !==
      JSON.stringify(filters.conditionsByRoomType)
    ) {
      setState(oldValues => ({
        ...oldValues,
        conditionsByRoomType,
      }));
    }
  };

  const handleReset = (resetFields: any) => {
    EventEmitter.emit('onResetPress');
    const updatedFilters = {
      ...filters,
      ...resetFields,
    };
    setState({
      label: '',
      priceDirty: false,
      showAverages: false,
      sortAsc: true,
      sortBy: '',
    });
    updateFilter(updatedFilters);
    handleSubmit(updatedFilters);
    sortFeed('id', true);
  };

  const sortFeed = (sortBy: string, sortAsc: boolean) => {
    if (sortBy) {
      props.feedFetchedHelper(false);
      props.sortFeedHelper(sortBy, sortAsc, props.feed);
    }
  };

  const sortButton = (label: string, sortBy: string) => {
    return (
      <Button
        className={
          state.sortBy === sortBy
            ? classes.selectedButton
            : classes.unSelectedButton
        }
        onClick={() => {
          onSortChange(label, sortBy);
        }}
      >
        {label}
        {state.sortBy === sortBy &&
          (state.sortAsc ? (
            <div className={classes.centerAlign}>
              <SortDescending size={24} color="#323232" weight="regular" />
            </div>
          ) : (
            <div className={classes.centerAlign}>
              <SortAscending size={24} color="#323232" weight="regular" />
            </div>
          ))}
      </Button>
    );
  };

  const photosByRoom: any[] = feedSearch.photosByRoom
    ? ROOM_TYPES.reduce((previousValue: any, currentValue: any) => {
        if (feedSearch.photosByRoom.includes(currentValue.rt)) {
          return previousValue.concat(currentValue.label);
        } else {
          return previousValue;
        }
      }, [])
    : [];

  // Calculate the average price, square ft. and days on market
  let listPriceSum = 0;
  let marketTimeSum = 0;
  let listSqSum = 0;
  for (let i = 0; i < feed.length; i += 1) {
    const listPrice = parseFloat(feed[i].listPrice);
    listSqSum += feed[i].squareFeet;
    listPriceSum += listPrice;
    marketTimeSum += parseInt(feed[i].marketTime, 10);
  }

  return (
    <div className={`${classes.root} no-mobile`}>
      <div className={classes.sortContainer}>
        <Button
          id="photo_order_button"
          className={classes.sortBy}
          onClick={() => toggleSortOption('filterRoomPhotoOrderOpen')}
        >
          {photosByRoom.length > 0 ? (
            <Camera size={24} color="#443461" weight="duotone" />
          ) : (
            <Camera size={24} color="#979797" weight="duotone" />
          )}
          {photosByRoom.length > 0 ? (
            <span id="room photo order button" className={classes.sortByText}>
              {photosByRoom.join(', ')}
            </span>
          ) : (
            <span id="room photo order button" className={classes.sortByText}>
              Room Photo Order
            </span>
          )}
          {filterRoomPhotoOrderOpen ? (
            <CaretUp
              id="room photo order button"
              size={16}
              color="#443461"
              weight="bold"
            />
          ) : (
            <CaretDown
              id="room photo order button"
              size={16}
              color="#443461"
              weight="bold"
            />
          )}
        </Button>
        <Button
          id="sort_button"
          className={classes.sortBy}
          onClick={() => toggleSortOption('filterSortOptionsOpen')}
        >
          {state.sortBy ? (
            <ArrowsDownUp size={24} color="#443461" weight="duotone" />
          ) : (
            <ArrowsDownUp size={24} color="#979797" weight="duotone" />
          )}
          <span id="sort button" className={classes.sortByText}>
            Sort By: {state.label}
          </span>
          {filterSortOptionsOpen ? (
            <CaretUp id="sort button" size={16} color="#443461" weight="bold" />
          ) : (
            <CaretDown
              id="sort button"
              size={16}
              color="#443461"
              weight="bold"
            />
          )}
        </Button>
        {filterRoomPhotoOrderOpen && (
          <SortByPhoto
            defaultPhotosOrder={filters.photosByRoom}
            defaultRoomQuality={filters.conditionsByRoomType}
            handleRoomFilter={handleRoomFilter}
            handleQualityFilter={handleRoomQualityFilter}
            toggleSortOption={toggleSortOption}
            handleReset={handleReset}
            handleSubmit={() => handleSubmit(filters)}
            onClose={() => toggleSortOption('filterRoomPhotoOrderOpen')}
          />
        )}
        {filterSortOptionsOpen && (
          <SortByGeneral
            mobile={mobile}
            sortButton={sortButton}
            sortRef={ref}
            onClose={() => toggleSortOption('filterSortOptionsOpen')}
          />
        )}
      </div>
    </div>
  );
}

const mapStateToProps = (state: any) => ({
  feed: state.feed.feed,
  feedFetched: state.feed.feedFetched,
  feedSearch: state.feed.feedSearch,
  filters: state.feed.filterSearch,
  token: state.auth.token,
  filterRoomPhotoOrderOpen: state.dialogs.filterRoomPhotoOrderOpen,
  filterSortOptionsOpen: state.dialogs.filterSortOptionsOpen,
});

const mapDispatchToProps = (dispatch: any) => ({
  feedFetchedHelper(status: boolean) {
    dispatch(feedFetchedHelper(status));
  },
  fetchMapFeed(
    mapCenter: string,
    token: string,
    boundingBox: string,
    useBoundingBox: boolean,
    feedSearch: any,
    usePlaceBoundary: boolean,
    fetchLargePhotoList: boolean,
    mapView: boolean,
  ) {
    dispatch(
      fetchMapFeed(
        mapCenter,
        token,
        boundingBox,
        useBoundingBox,
        feedSearch,
        usePlaceBoundary,
        fetchLargePhotoList,
        mapView,
      ),
    );
  },
  sortFeedHelper(sortBy: string, sortAsc: boolean, feed: Object[]) {
    dispatch(sortFeedHelper(sortBy, sortAsc, feed));
  },
  updateFilter(filter: any) {
    dispatch(updateFilterSearch(filter));
  },
  updateFilterDialog(dialog: string) {
    dispatch({
      type: UpdateFilterDialog,
      dialog,
    });
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(SortForm);
